<template>
  <div class="select-time-page">
    <!-- Modern Header -->
    <div class="page-header">
      <div class="header-content">
        <h1>📅 Randevu Saati Seçimi</h1>
        <p class="header-subtitle">{{ lessonName }} dersi için {{ teacherName }} Öğretmenden randevu alın</p>

      </div>
      <div class="header-actions">
        <button @click="goBack" class="btn-back">
          ← Geri Dön
        </button>
      </div>
      </div>

    <main class="page-content">
      <!-- Lesson Info Card -->
      <div class="lesson-info-section">
        <div class="lesson-info-card">
          <div class="lesson-icon">📚</div>
          <div class="lesson-details">
            <h2>{{ lessonName }}</h2>
            <p class="teacher-info">{{ teacherName }}</p>
            <p class="instruction">Randevu almak istediğiniz tarihi ve saati seçiniz</p>
          </div>
        </div>
      </div>



      <!-- Haftalık Takvim -->
      <div class="weekly-calendar">
        <div class="calendar-header">
          <h3>📅 Haftalık Takvim</h3>
          <p class="calendar-note">
            <strong>Not:</strong> Bugün için {{ todayDayStart }}’den önce ve sonraki günler için randevu alınabilir.
          </p>
      </div>
      
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Müsaitlik bilgileri yükleniyor...</p>
      </div>
      
        <div v-else>
          <TeacherAvailabilityDisplay 
            :teacherId="teacherId"
            :viewMode="'select'"
            :compact="false"
            :refresh="refreshAvailability"
            :onSlotClick="handleSlotClick"
          />
        </div>
      </div>
      
      <!-- Seçilen Randevu Modal -->
      <div v-if="selectedAppointment" class="modal-overlay" @click="closeAppointmentModal">
        <div class="modal-content appointment-modal" @click.stop>
          <div class="modal-header">
            <h3>✅ Seçilen Randevu</h3>
            <button @click="closeAppointmentModal" class="modal-close">&times;</button>
      </div>
      
          <div class="modal-body">
      
            <div class="summary-details">
              <div class="summary-item">
                <span class="label">Ders:</span>
                <span class="value">{{ lessonName }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Hoca:</span>
                <span class="value">{{ teacherName }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Tarih:</span>
                <span class="value">{{ formatSelectedDate(selectedAppointment.date) }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Saat:</span>
                <span class="value">{{ selectedAppointment.time }}</span>
              </div>
        </div>
      </div>

          <div class="modal-actions">
            <button @click="clearSelection" class="clear-button">Seçimi Temizle</button>
        <button @click="createAppointment" class="create-button" :disabled="isSubmitting">
              {{ isSubmitting ? '🔄 Randevu Oluşturuluyor...' : '🎯 Randevu Oluştur' }}
        </button>
          </div>
        </div>
      </div>

      <!-- Müsaitlik Durumu Açıklaması -->
      <div class="availability-legend">
        <h4>📋 Müsaitlik Durumu Açıklaması</h4>
        <div class="legend-items">
          <div class="legend-item">
            <div class="legend-color available"></div>
            <span>Müsait - Randevu alınabilir (Yeşil)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color booked"></div>
            <span>Alınmış - Başka öğrenci tarafından alınmış (Mor)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color disabled"></div>
            <span>Öğle Arası - Bu saatte ders yapılmaz (Beyaz)</span>
          </div>
          <div class="legend-item">
            <div class="legend-color not-available"></div>
            <span>Müsait Değil - Öğretmen bu saatte müsait değil (Beyaz)</span>
          </div>
        </div>
      </div>
    </main>

    <!-- Haftalık Kısıtlama Modal -->
    <div v-if="showWeeklyLimitModal" class="modal-overlay weekly-limit-overlay" @click="closeWeeklyLimitModal">
      <div class="modal-content weekly-limit-modal" @click.stop>
        <div class="modal-header weekly-limit-header">
          <div class="header-icon">📅</div>
          <h3>Haftalık Takvim</h3>
          <button @click="closeWeeklyLimitModal" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <!-- Not Mesajı -->
          <div class="info-message">
            <span class="message-icon">ℹ️</span>
            <span class="message-text">Bugün için {{ todayDayStart }}'den önce ve sonraki günler için randevu alınabilir. Slot süreleri {{ weekdaySlotDuration }} dakikadır.</span>
          </div>
          
          <!-- Haftalık Limit Uyarısı -->
          <div class="warning-message">
            <span class="message-icon">⚠️</span>
            <span class="message-text">
              Bir hafta içinde en fazla {{ weeklyLimitData?.maxCount || 3 }} ders alabilirsiniz 
              (Mevcut: {{ weeklyLimitData?.currentCount || 0 }}, İptal edilmiş randevular sayılmıyor)
            </span>
          </div>
        </div>
        
        <div class="modal-actions">
          <button @click="closeWeeklyLimitModal" class="btn-close">Kapat</button>
        </div>
      </div>
    </div>

    <!-- Hata Modal -->
    <div v-if="error" class="error-modal-overlay" @click="closeErrorModal">
      <div class="error-modal-content" @click.stop>
        <div class="error-modal-header">
          <div class="error-header-icon">⚠️</div>
          <h3>Hata Oluştu</h3>
        </div>
        <div class="error-modal-body">
          <p>{{ error }}</p>
          <div class="auto-close-info">
            <span class="auto-close-icon">⏰</span>
            <span class="auto-close-message">Bu mesaj 5 saniye sonra otomatik olarak kapanacak</span>
          </div>
        </div>
        <div class="error-modal-actions">
          <button @click="closeErrorModal" class="btn-close">Kapat</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Başarı Popup -->
  <div v-if="showSuccessPopup" class="success-popup-overlay" @click="closeSuccessPopup">
    <div class="success-popup" @click.stop>
      <div class="success-popup-header">
        <div class="success-icon">🎉</div>
        <h3>{{ successTitle }}</h3>
      </div>
      <div class="success-popup-body">
        <p>{{ successMessage }}</p>
        <div class="success-info">
          <p>2 saniye sonra öğrenci paneline yönlendirileceksiniz...</p>
        </div>
      </div>
      <div class="success-popup-actions">
        <button @click="closeSuccessPopup" class="btn-success">
          Tamam
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import TeacherAvailabilityDisplay from '@/components/TeacherAvailabilityDisplay.vue'
import { generateWeekdaySlots, generateWeekendSlots } from '@/utils/slotGenerator'
import axios from 'axios'

export default {
  name: 'SelectTimeView',
  components: {
    TeacherAvailabilityDisplay
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    const teacherId = computed(() => route.params.teacherId)
    const teacherName = ref(route.query?.teacherName || 'Seçilen Hoca')
    const lessonId = computed(() => route.query?.lessonId || '')
    const lessonName = ref(route.query?.lessonName || 'Seçilen Ders')
    
    // Mevcut kullanıcı bilgisini al
    const currentUser = computed(() => store.getters.getCurrentUser)
    const studentId = computed(() => currentUser.value?.id)
    
    const loading = ref(false)
    const error = ref('')
    const isSubmitting = ref(false)
    const refreshAvailability = ref(false)
    
    const availability = ref({})
    
    // Seçilen randevu
    const selectedAppointment = ref(null)
    
    // Haftalık kısıtlama modal durumu
    const showWeeklyLimitModal = ref(false)
    const weeklyLimitData = ref({
      currentCount: 0,
      maxCount: 3
    })
    
    // Başarı popup durumu
    const showSuccessPopup = ref(false)
    const successMessage = ref('')
    const successTitle = ref('')
    
    // 🚨 Güvenlik için sistem durumu kontrolü
    const systemStatus = ref(null)
    const systemStatusInterval = ref(null)
    
    // 🔄 Anında yenileme için interval
    const availabilityRefreshInterval = ref(null)

    const weekdays = [
      { key: 'monday', name: 'Pazartesi' },
      { key: 'tuesday', name: 'Salı' },
      { key: 'wednesday', name: 'Çarşamba' },
      { key: 'thursday', name: 'Perşembe' }
    ]

    const weekends = [
      { key: 'saturday', name: 'Cumartesi' },
      { key: 'sunday', name: 'Pazar' }
    ]

    // Slot ayarları store'dan gelir, mount'ta yüklenir (fetch)
    const slotSettings = computed(() => store.getters.getSlotSettings)
    const weekdaySlots = computed(() => generateWeekdaySlots(slotSettings.value))
    const weekendSlots = computed(() => generateWeekendSlots(slotSettings.value))
    const weekdaySlotDuration = computed(() => slotSettings.value?.slot_duration_weekday || 20)
    const todayDayStart = computed(() => {
      const isWeekend = [0, 6].includes(new Date().getDay())
      return (isWeekend
        ? slotSettings.value?.day_start_weekend
        : slotSettings.value?.day_start_weekday) || '08:00'
    })



    // Müsaitlik verilerini getir
    const fetchAvailability = async () => {
      try {
        loading.value = true
        // error.value = '' // Bu satırı kaldırdık - error popup'ını temizlemesin
        
        // TeacherAvailabilityDisplay component'inden veri alınacak
        // Bu fonksiyon sadece retryLoad için gerekli
        
        
        // Component otomatik olarak veri yükleyecek
        loading.value = false
      } catch (err) {
        console.error('Müsaitlik verileri yüklenirken hata:', err)
        error.value = 'Müsaitlik verileri yüklenirken bir hata oluştu'
        loading.value = false
      }
    }

    // Error state değiştiğinde seçilen randevu modal'ını kapat ve otomatik kapatma başlat
    watch(error, (newError) => {
      if (newError && selectedAppointment.value) {
        selectedAppointment.value = null
      }
      
      // Error popup'ı gösterildiğinde otomatik kapatma başlat
      if (newError) {
        autoCloseError()
      }
    })

    // İlk yüklemede sadece kullanıcı bilgisi kontrolü yap
    onMounted(async () => {
      const now = new Date()
      const currentDay = now.getDay() // 0 = Pazar, 1 = Pazartesi, ...
      const currentHour = now.getHours()

      // Slot ayarlarını yükle (henüz store'da yoksa)
      if (!slotSettings.value) {
        await store.dispatch('fetchSlotSettings')
      }

      // Kullanıcı bilgisi kontrolü

      // 🔄 Store'dan kullanıcı bilgisini yeniden yükle
      const token = localStorage.getItem('token')
      if (token) {
        try {
          await store.dispatch('fetchCurrentUser')
        } catch (error) {
          console.error('Kullanıcı bilgisi yüklenemedi:', error)
        }
      }
      
      if (!studentId.value) {
        console.warn('⚠️ StudentID bulunamadı, login sayfasına yönlendiriliyor')
        router.push('/login')
        return
      }
      
      // 🚨 Güvenlik: Sistem durumunu kontrol et ve sürekli izle
      await checkSystemStatus()
      startSystemStatusMonitoring()
      
      // 🔄 Anında yenileme başlat
      startAvailabilityRefresh()
      
      // Haftalık limit kontrolü yap
      await checkWeeklyLimit()
      
      // Hafta kontrolü artık TeacherAvailabilityDisplay component'inde yapılıyor
      
    })
    
    onUnmounted(() => {
      // 🚨 Güvenlik: Interval'ı temizle
      stopSystemStatusMonitoring()
      
      // 🔄 Anında yenilemeyi durdur
      stopAvailabilityRefresh()
    })







    const getSlotClass = (dayKey, slotId) => {
      if (slotId.includes('12:00-12:50') || slotId.includes('12:50-13:30')) {
        return 'disabled'
      }
      
      const daySlots = availability.value[dayKey]
      if (!daySlots) return 'not-available'
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      if (!slot) return 'not-available'
      
      if (slot.is_booked) return 'booked'
      return 'available'
    }

    const getSlotStatus = (dayKey, slotId) => {
      if (slotId.includes('12:00-12:50') || slotId.includes('12:50-13:30')) {
        return 'Öğle Arası'
      }
      
      const daySlots = availability.value[dayKey]
      if (!daySlots) return '✗ Müsait Değil'
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      if (!slot) return '✗ Müsait Değil'
      
      if (slot.is_booked) return '✗ Alınmış'
      return '✓ Müsait'
    }

    const selectTimeSlot = (dayKey, slotId, time) => {
      const daySlots = availability.value[dayKey]
      if (!daySlots) return
      
      const slot = daySlots.find(s => s.time_slot === slotId)
      if (!slot || slot.is_booked) return
      
      // Seçilen günün tarihini hesapla (bu hafta içinde)
      const selectedDate = new Date()
      const dayToNumber = { 'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4, 'saturday': 6, 'sunday': 0 }
      const currentDay = selectedDate.getDay()
      const targetDay = dayToNumber[dayKey]
      
      let daysToAdd = 0 // Varsayılan değer
      
      if (targetDay !== undefined) {
        // Bu hafta içinde mi kontrol et
        daysToAdd = targetDay - currentDay
        
        // Eğer hedef gün bu hafta geçtiyse, gelecek haftaya git
        if (daysToAdd < 0) {
          daysToAdd += 7
        }
        
        selectedDate.setDate(selectedDate.getDate() + daysToAdd)
      }
      
      selectedAppointment.value = {
        dayKey,
        slotId,
        time,
        date: selectedDate,
        dayName: weekdays.find(d => d.key === dayKey)?.name || weekends.find(d => d.key === dayKey)?.name
      }
      
      
    }

    // TeacherAvailabilityDisplay'dan gelen tıklamayı işle
    const handleSlotClick = async (dayKey, slotId, slotTime) => {
      // Önce haftalık limit kontrolü yap
      const canBook = await checkWeeklyLimit()
      if (!canBook) {
        return // Modal zaten açıldı
      }
      
      // Seçilen günün tarihini hesapla (bugünden itibaren)
      const selectedDate = new Date()
      const dayToNumber = { 'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4, 'saturday': 6, 'sunday': 0 }
      const currentDay = selectedDate.getDay()
      const targetDay = dayToNumber[dayKey]
      
      let daysToAdd = 0 // Varsayılan değer
      
      if (targetDay !== undefined) {
        // Bu hafta içinde mi kontrol et
        daysToAdd = targetDay - currentDay
        
        // Eğer hedef gün bu hafta geçtiyse, gelecek haftaya git
        if (daysToAdd < 0) {
          daysToAdd += 7
        }
        
        selectedDate.setDate(selectedDate.getDate() + daysToAdd)
      }
      
      selectedAppointment.value = {
        dayKey,
        slotId,
        time: slotTime,
        date: selectedDate,
        dayName: weekdays.find(d => d.key === dayKey)?.name || weekends.find(d => d.key === dayKey)?.name
      }
      
      
    }



    const formatSelectedDate = (date) => {
      return date.toLocaleDateString('tr-TR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    }

    const clearSelection = () => {
      selectedAppointment.value = null
    }

    const closeAppointmentModal = () => {
      selectedAppointment.value = null
    }

    const closeWeeklyLimitModal = () => {
      showWeeklyLimitModal.value = false
    }

    const pendingRedirect = ref(null)
    const closeErrorModal = () => {
      error.value = ''
      if (pendingRedirect.value) {
        const target = pendingRedirect.value
        pendingRedirect.value = null
        router.push(target)
      }
    }
    
    // Error popup'ını otomatik kapatma fonksiyonu
    const autoCloseError = () => {
      if (error.value) {
        setTimeout(() => {
          if (error.value) {
            error.value = ''
          }
        }, 5000) // 5 saniye sonra otomatik kapat
      }
    }
    
    const closeSuccessPopup = () => {
      showSuccessPopup.value = false
      successMessage.value = ''
      successTitle.value = ''
    }

    // Haftalık kısıtlama kontrolü
    const checkWeeklyLimit = async () => {
      try {
        // Mevcut haftadaki randevuları getir
        const response = await axios.get(`https://api.modernkurs.com/api/appointments?user_id=${studentId.value}`)
        if (response.data && response.data.success !== false) {
          const data = response.data
          const appointments = Array.isArray(data) ? data : (data.appointments || [])
          
          // Bu haftadaki onaylanmış VE öğrenci katılmadı durumundaki randevuları say
          const now = new Date()
          const weekStart = getWeekStartDate(now)
          const weekEnd = new Date(weekStart)
          weekEnd.setDate(weekStart.getDate() + 7)
          
          const weeklyAppointments = appointments.filter(apt => {
            const aptDate = new Date(apt.appointment_time)
            const isInCurrentWeek = aptDate >= weekStart && aptDate < weekEnd
            const isCountableStatus = apt.status !== 'iptal edildi' // Tüm randevular (geçmiş dahil), sadece iptal edilmiş hariç
            return isInCurrentWeek && isCountableStatus
          })
          
          weeklyLimitData.value.currentCount = weeklyAppointments.length
          
          
          
          // Limit kontrolü
          if (weeklyAppointments.length >= weeklyLimitData.value.maxCount) {
            showWeeklyLimitModal.value = true
            return false
          }
          
          
          return true
        }
        return true
      } catch (err) {
        console.error('Haftalık limit kontrolü hatası:', err)
        return true
      }
    }

    // Hafta başlangıç tarihini hesapla (Backend mantığıyla uyumlu)
    const getWeekStartDate = (date) => {
      // Turkey timezone (+3) için tarihi ayarla
      const turkeyDate = new Date(date.toLocaleString("en-US", {timeZone: "Europe/Istanbul"}))
      
      // Pazar 19:00'dan sonra VEYA Pazartesi'den itibaren gelecek haftayı göster
      const isSundayAfter7PM = turkeyDate.getDay() === 0 && turkeyDate.getHours() >= 19 && turkeyDate.getMinutes() >= 0
      const isAfterReset = isSundayAfter7PM || turkeyDate.getDay() !== 0
      
      // Eğer reset sonrasıysa, gelecek haftayı hesapla
      if (isAfterReset) {
        date = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 gün ekle
        turkeyDate.setDate(turkeyDate.getDate() + 7)
      }
      
      const weekday = turkeyDate.getDay()
      // Pazar = 0, Pazartesi = 1, ..., Cumartesi = 6
      // Hafta başlangıcı Pazartesi günü olacak
      const daysToMonday = weekday === 0 ? 6 : weekday - 1
      
      // Pazartesi gününü hesapla
      const monday = new Date(turkeyDate)
      monday.setDate(turkeyDate.getDate() - daysToMonday)
      
      // Debug için
      
      
      return monday
    }

    const createAppointment = async () => {
      if (!selectedAppointment.value) {
        error.value = 'Lütfen bir saat seçin.'
        return
      }

      // StudentID kontrolü
      if (!studentId.value) {
        error.value = 'Öğrenci bilgisi bulunamadı. Lütfen tekrar giriş yapın.'
        pendingRedirect.value = '/login'
        return
      }

      // 🚨 GÜVENLİK: Randevu oluşturmadan önce sistem durumunu tekrar kontrol et
      const isSystemReady = await checkSystemStatus()

      if (!isSystemReady) {
        error.value = 'Sistem bakım moduna alındı. Randevu oluşturulamaz. Ana sayfaya yönlendiriliyorsunuz.'
        pendingRedirect.value = '/panel/ogrenci'
        return
      }

      // 🚨 GÜVENLİK: Seçilen slot'ın hala müsait olup olmadığını kontrol et
      if (selectedAppointment.value) {
        const { dayKey, slotId } = selectedAppointment.value
        const daySlots = availability.value[dayKey]

        if (daySlots) {
          const slot = daySlots.find(s => s.time_slot === slotId)
          if (slot && slot.is_booked) {
            error.value = 'Seçilen saat artık müsait değil. Lütfen başka bir saat seçin.'
            selectedAppointment.value = null
            return
          }
        }
      }
      
      
      
      isSubmitting.value = true
      // error.value = '' // Bu satırı kaldırdık - error popup'ını temizlemesin
      
      try {
        // Randevu zamanını oluştur
        const appointmentTime = new Date(selectedAppointment.value.date)
        const [hours, minutes] = selectedAppointment.value.time.split('-')[0].split('.')
        appointmentTime.setHours(parseInt(hours), parseInt(minutes), 0, 0)
        
        const appointmentTimeStr = appointmentTime.toISOString()
        
        
        
        // Not: student_id backend'de JWT'den alınıyor; body'de göndermeye gerek yok.
        const response = await axios.post('https://api.modernkurs.com/api/appointments', {
          lesson_id: parseInt(lessonId.value),
          teacher_id: parseInt(teacherId.value),
          appointment_time: appointmentTimeStr
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        if (response.data.success) {
          // Başarı popup'ını göster
          successTitle.value = '🎉 Randevu Başarıyla Oluşturuldu!'
          successMessage.value = `Randevu, ${lessonName.value} dersi için ${teacherName.value} Öğretmenden başarıyla alındı!`
          showSuccessPopup.value = true
          
          // 2 saniye sonra otomatik olarak öğrenci paneline yönlendir
          setTimeout(async () => {
            // Randevuları yeniden yükle
            try {
              await store.dispatch('fetchAppointments', studentId.value)
              
            } catch (err) {
              console.warn('⚠️ Randevular yeniden yüklenirken hata:', err)
            }
            
            // Müsaitlik takvimini yenile - daha güçlü refresh
            refreshAvailability.value = true
            
            // 500ms sonra false yap
            setTimeout(() => {
              refreshAvailability.value = false
            }, 500)
            
            // Ek olarak 1 saniye sonra tekrar refresh yap
            setTimeout(() => {
              refreshAvailability.value = true
              setTimeout(() => {
                refreshAvailability.value = false
              }, 500)
            }, 1000)
            
          router.push('/panel/ogrenci')
          }, 2000)
        } else {
          error.value = response.data.error || 'Randevu oluşturulamadı'
        }
      } catch (err) {
        console.error('Randevu oluşturma hatası:', err)
        // Axios error'unda backend'den gelen hata mesajını al
        if (err.response && err.response.data && err.response.data.error) {
          error.value = err.response.data.error
        } else {
          error.value = 'Randevu oluşturulurken bir hata oluştu'
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const retryLoad = () => {
      fetchAvailability()
    }

    const goBack = () => {
      // Öğrenci paneli altındaki öğretmen seçim sayfasına geri dön
      router.push({ 
        name: 'StudentSelectTeacher', 
        params: { lessonId: lessonId.value },
        query: {
          lessonName: lessonName.value
        }
      })
    }
    
    // 🚨 Güvenlik: Sistem durumunu kontrol et
    const checkSystemStatus = async () => {
      try {
        const response = await axios.get('https://api.modernkurs.com/api/system/status/user')
        if (response.data.success) {
          systemStatus.value = response.data
          
          // Sistem hazır değilse güvenlik uyarısı ver
          if (!response.data.can_book) {
            
            error.value = 'Sistem bakım moduna alındı. Ana sayfaya yönlendiriliyorsunuz.'
            pendingRedirect.value = '/panel/ogrenci'
            return false
          }
          
          return true
        } else {
          console.error('❌ Sistem durumu alınamadı:', response.data.message)
          return false
        }
      } catch (error) {
        console.error('❌ Sistem durumu kontrol hatası:', error)
        return false
      }
    }
    
    // 🚨 Güvenlik: Sistem durumunu sürekli izle
    const startSystemStatusMonitoring = () => {
      // Her 10 saniyede bir sistem durumunu kontrol et
      systemStatusInterval.value = setInterval(async () => {
        const isSystemReady = await checkSystemStatus()
        if (!isSystemReady) {
          // Sistem hazır değilse interval'ı durdur
          stopSystemStatusMonitoring()
        }
      }, 10000) // 10 saniye
    }
    
    // 🔄 Anında yenileme için müsaitlik verilerini sürekli güncelle
    const startAvailabilityRefresh = () => {
      // Her 5 saniyede bir müsaitlik verilerini yenile
      const availabilityInterval = setInterval(async () => {
        if (systemStatus.value && systemStatus.value.can_book) {
          
          await fetchAvailability()
        } else {
          // Sistem hazır değilse yenilemeyi durdur
          clearInterval(availabilityInterval)
        }
      }, 5000) // 5 saniye
      
      // Interval'ı temizlemek için ref'e kaydet
      availabilityRefreshInterval.value = availabilityInterval
    }
    
    // 🔄 Müsaitlik yenilemeyi durdur
    const stopAvailabilityRefresh = () => {
      if (availabilityRefreshInterval.value) {
        clearInterval(availabilityRefreshInterval.value)
        availabilityRefreshInterval.value = null
      }
    }
    
    // 🚨 Güvenlik: Sistem durumunu izlemeyi durdur
    const stopSystemStatusMonitoring = () => {
      if (systemStatusInterval.value) {
        clearInterval(systemStatusInterval.value)
        systemStatusInterval.value = null
      }
    }

    return {
      teacherId,
      teacherName,
      lessonId,
      lessonName,
      currentUser,
      studentId,
      loading,
      showSuccessPopup,
      successMessage,
      successTitle,
      error,
      isSubmitting,
      availability,
      selectedAppointment,
      weekdays,
      weekends,
      weekdaySlots,
      weekendSlots,
      weekdaySlotDuration,
      todayDayStart,
      getSlotClass,
      getSlotStatus,
      selectTimeSlot,
      handleSlotClick,
      formatSelectedDate,
      clearSelection,
      closeAppointmentModal,
      closeWeeklyLimitModal,
      closeErrorModal,
      autoCloseError,
      closeSuccessPopup,
      createAppointment,
      fetchAvailability,
      retryLoad,
      goBack,
      // 🚨 Güvenlik fonksiyonları
      systemStatus,
      checkSystemStatus,
      startSystemStatusMonitoring,
      stopSystemStatusMonitoring,
      // 🔄 Anında yenileme fonksiyonları
      startAvailabilityRefresh,
      stopAvailabilityRefresh,
      // 📋 Template'de kullanılan property'ler
      refreshAvailability,
      showWeeklyLimitModal
    }
  }
}
</script>

<style scoped>
/* Modern Select Time Page Styles */
.select-time-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2.5rem;
  font-weight: 700;
}

.header-subtitle {
  margin: 0 0 1rem 0;
  opacity: 0.9;
  font-size: 1.1rem;
}



.header-actions { display: flex; align-items: center; }

.btn-back {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Page Content */
.page-content {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* SelectTime responsive tasarım */
@media (max-width: 1024px) {
  .page-content {
    max-width: 100%;
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .page-content {
    padding: 1rem;
    margin: 0;
  }
  
  .page-header {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
  text-align: center;
}

  .header-content h1 {
    font-size: 2rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .lesson-info-card {
    padding: 1rem;
    flex-direction: column;
  text-align: center;
}

  .lesson-icon {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .weekly-calendar {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .page-content {
  padding: 0.75rem;
    margin: 0;
  }
  
  .page-header {
    padding: 1rem;
  }
  
  .header-content h1 {
    font-size: 1.75rem;
  }
  
  .lesson-info-card {
    padding: 0.75rem;
    margin-bottom: 1.5rem;
  }
  
  .weekly-calendar {
    padding: 1rem;
  }
}

.lesson-info-card {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.lesson-icon {
  font-size: 3rem;
  margin-right: 1.5rem;
  color: #4CAF50;
}

.lesson-details h2 {
  margin-bottom: 0.5rem;
  color: #333;
}

.teacher-info {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 0.8rem;
}

.instruction {
  font-size: 0.9rem;
  color: #888;
}



.weekly-calendar {
  background-color: #fff;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.calendar-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.calendar-header h3 {
  color: #4CAF50;
  margin-bottom: 0.8rem;
}

.calendar-note {
  font-size: 0.9rem;
  color: #888;
  margin-top: 0.5rem;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.loading-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left: 4px solid #4CAF50;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.weekday-section, .weekend-section {
  padding: 1rem;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: #fdfdfd;
  margin-bottom: 1rem;
}

.weekday-section h4, .weekend-section h4 {
  color: #4CAF50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #eee;
}

.days-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-column {
  flex: 1;
  min-width: 120px;
  text-align: center;
}

.day-header {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.8rem;
}

.time-slots {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.time-slot {
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.time-slot:hover {
  background-color: #f0f0f0;
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.time-slot.selected {
  background-color: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.time-slot.available {
  background-color: #e8f5e9; /* Light green */
  border-color: #a5d6a7;
  color: #2e7d32; /* Darker green */
}

.time-slot.booked {
  background-color: #ffebee; /* Light red */
  border-color: #ef5350;
  color: #c62828; /* Darker red */
}

.time-slot.disabled {
  background-color: #f5f5f5;
  border-color: #eee;
  color: #9e9e9e;
  cursor: not-allowed;
  opacity: 0.7;
}

.time-slot.not-available {
  background-color: #fbe9e7; /* Light orange */
  border-color: #ffab91;
  color: #e65100; /* Darker orange */
}

.time-text {
  font-size: 0.9rem;
  font-weight: bold;
}

.break-text {
  font-size: 0.7rem;
  color: #888;
  margin-top: 0.2rem;
}

.slot-status {
  font-size: 0.75rem;
  font-weight: bold;
  position: absolute;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  white-space: nowrap;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
}

.modal-content {
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease-out;
}

.appointment-modal {
  max-width: 600px;
}

.weekly-limit-modal {
  max-width: 500px;
}

.weekly-limit-overlay {
  z-index: 1500;
}

.weekly-limit-header {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.header-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.weekly-limit-header h3 {
  display: flex;
  align-items: center;
  margin: 0;
}

.modal-header {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 15px 15px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 1.5rem;
}

.modal-actions {
  padding: 1.5rem;
  display: flex;
  justify-content: space-around;
  gap: 1rem;
  border-top: 1px solid #eee;
}

/* Summary Details */
.summary-details {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 1.5rem;
}

/* Error Modal */
.error-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease-in-out;
}

.error-modal-content {
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  animation: slideIn 0.3s ease-out;
}

.error-modal-header {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 15px 15px 0 0;
  text-align: center;
}

.error-header-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.error-modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.error-modal-body {
  padding: 1.5rem;
  text-align: center;
}

.error-modal-body p {
  margin: 0;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.5;
}

.error-modal-actions {
  padding: 1.5rem;
  text-align: center;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
  gap: 1rem;
}

/* Auto Close Info */
.auto-close-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fff3e0;
  border: 1px solid #ff9800;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #e65100;
}

.auto-close-icon {
  font-size: 1.1rem;
  animation: pulse 2s infinite;
}

.auto-close-message {
  font-weight: 500;
}

/* Close Button */
.btn-close {
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 100px;
  box-shadow: 0 4px 15px rgba(158, 158, 158, 0.3);
}

.btn-close:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(158, 158, 158, 0.4);
}

/* Weekly Limit Modal Messages */
.info-message, .warning-message {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  line-height: 1.4;
}

.info-message {
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  color: #1565c0;
}

.warning-message {
  background-color: #fff3e0;
  border: 1px solid #ff9800;
  color: #e65100;
}

.message-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.message-text {
  flex: 1;
  font-weight: 500;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  font-size: 1.1rem;
  color: #555;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item .label {
  font-weight: bold;
  color: #333;
}

.summary-item .value {
  font-weight: bold;
  color: #4CAF50;
}

/* Buttons */
.clear-button, .create-button {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 120px;
}

.clear-button {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(244, 67, 54, 0.3);
}

.clear-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(244, 67, 54, 0.4);
}

.create-button {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.create-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.create-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-retry {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 120px;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.btn-retry:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.availability-legend {
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: #fdfdfd;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.availability-legend h4 {
  color: #4CAF50;
  margin-bottom: 1rem;
  text-align: center;
}

.legend-items {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #555;
}

.legend-color {
  width: 15px;
  height: 15px;
  border-radius: 3px;
}

.legend-color.available {
  background-color: #4caf50; /* Yeşil - Müsait saatler */
}

.legend-color.booked {
  background-color: #9c27b0; /* Mor - Alınmışlar */
}

.legend-color.disabled {
  background-color: #f5f5f5; /* Beyaz - Öğle arası */
}

.legend-color.not-available {
  background-color: #ffffff; /* Beyaz - Müsait değiller */
}

/* Responsive Tasarım */
@media (max-width: 768px) {
  .main-content {
  padding: 1rem;
  }
  

  
  .days-row {
    flex-direction: column;
  }
  
  .day-column {
    min-width: 100%;
  }
  
  .time-slot {
    padding: 0.5rem 0.75rem;
  }
  
  .time-text {
    font-size: 0.8rem;
  }
  
  .slot-status {
    font-size: 0.65rem;
    padding: 0.1rem 0.4rem;
  }
  
  .summary-actions {
    flex-direction: column;
  }
  
  .legend-items {
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .header-content h1 {
    font-size: 1.5rem;
  }
  
  .lesson-info-card {
    flex-direction: column;
  text-align: center;
  }
  
  .lesson-icon {
    margin-right: 0;
  margin-bottom: 1rem;
  }
  
  .calendar-grid {
    grid-template-columns: 1fr;
  }
}

/* Başarı Popup Stilleri */
.success-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease-out;
}

.success-popup {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  max-width: 500px;
  width: 90%;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

.success-popup-header {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  padding: 2rem 2rem 1.5rem;
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.success-popup-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.success-popup-body {
  padding: 2rem;
  text-align: center;
}

.success-popup-body p {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: #333;
}

.success-info {
  background: #e8f5e8;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.success-info p {
  margin: 0;
  color: #2e7d32;
  font-size: 0.95rem;
  font-weight: 500;
}

.success-popup-actions {
  padding: 1.5rem 2rem 2rem;
  text-align: center;
  border-top: 1px solid #eee;
}

.btn-success {
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  min-width: 120px;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4);
}

.btn-success:active {
  transform: translateY(0);
}

/* Animasyonlar */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Mobil responsive */
@media (max-width: 768px) {
  .success-popup {
    max-width: 90%;
    margin: 1rem;
  }
  
  .success-popup-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  .success-icon {
    font-size: 3rem;
  }
  
  .success-popup-header h3 {
    font-size: 1.3rem;
  }
  
  .success-popup-body {
    padding: 1.5rem;
  }
  
  .success-popup-actions {
    padding: 1rem 1.5rem 1.5rem;
  }
  
  .btn-success {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    min-width: auto;
  }
}

/* 🔄 Anında Yenileme ve Güvenlik Bilgi Stilleri */
.refresh-info {
  margin-top: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  border: 1px solid rgba(33, 150, 243, 0.2);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
}

.refresh-status-indicator,
.security-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.security-info {
  margin-bottom: 0;
}

.refresh-icon,
.security-icon {
  font-size: 1.1rem;
  animation: pulse 2s infinite;
}

.refresh-message,
.security-message {
  font-size: 0.875rem;
  color: #1565c0;
  font-weight: 500;
}

/* Pulse animasyonu */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

/* Responsive için refresh-info */
@media (max-width: 768px) {
  .refresh-info {
    padding: 0.75rem;
    margin-top: 1rem;
  }
  
  .refresh-message,
  .security-message {
    font-size: 0.8rem;
  }
}
</style> 